<script lang="ts" setup>
  import type { DisputeReason } from '@api/models';
  import { storeToRefs } from 'pinia';
  import { computed, nextTick, onBeforeMount, ref, useTemplateRef } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import { Button } from '@ui/components/button';
  import type { ButtonVariants } from '@ui/components/button';
  import { ComboSelect } from '@ui/components/combo-select';
  import { useLoader } from '@ui/components/combo-select/useLoader';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import DisputeReasonDialog from '@/components/disputes/DisputeReasonDialog.vue';
  import { useDisputeStore, useProfile } from '@/store';

  interface Props {
    disabled?: boolean;
    disabledFn?: (reason: DisputeReason) => boolean;
    initialOpen?: boolean;
    maxOptionWidth?: HTMLAttributes['class'];
    monotype?: boolean;
    onUpdate?: (disputeReason: DisputeReason) => void;
    reasonId?: string;
    showInactive?: boolean;
    size?: ButtonVariants['size'];
    triggerClass?: HTMLAttributes['class'];
  }

  const props = withDefaults(defineProps<Props>(), {
    disabledFn: (reason: DisputeReason) => !reason.is_active,
    maxOptionWidth: 'max-w-70',
  });

  const emit = defineEmits<{
    (e: 'update', DisputeReason): void;
    (e: 'close'): void;
  }>();

  const isOpen = ref(false);
  const profileStore = useProfile();
  const selectedReason = ref<DisputeReason>();
  const disputeStore = useDisputeStore();
  const { activeReasons, reasons } = storeToRefs(disputeStore);
  const ReasonDialogRef =
    useTemplateRef<InstanceType<typeof DisputeReasonDialog>>('reasonDialogRef');

  const reasonOptions = computed(() => {
    if (props.showInactive) return reasons.value;
    return activeReasons.value;
  });

  const addNewDisputeReason = () => {
    ReasonDialogRef.value?.show();
    isOpen.value = false;
  };

  const updateNewDisputeReason = (reason: DisputeReason) => {
    selectedReason.value = reason;
    emit('update', reason);

    disputeStore.addReason(reason);
  };

  const reasonLoader = useLoader({
    load: async (args?) => {
      if (!args?.search) {
        return reasonOptions.value as DisputeReason[];
      }

      const searchTerm = args.search.toLowerCase();
      return reasonOptions.value.filter(
        (reason) =>
          reason.name?.toLowerCase()?.includes(searchTerm) ||
          reason.code?.toLowerCase()?.includes(searchTerm),
      ) as DisputeReason[];
    },
  });

  onBeforeMount(async () => {
    if (!reasonOptions.value.length) await disputeStore.loadReasons();

    if (props.reasonId) {
      selectedReason.value = disputeStore.getReasonById(props.reasonId);
    }

    if (props.initialOpen) nextTick(() => (isOpen.value = true));
  });
</script>

<template>
  <ComboSelect
    v-bind="$attrs"
    v-model="selectedReason"
    v-model:open="isOpen"
    :loader="reasonLoader"
    searchable
    placeholder="Select Reason"
    search-placeholder="Select reason..."
    option-identifier="id"
    variant="dropdown"
    :size="size"
    :disabled="disabled"
    :disabled-fn="disabledFn"
    @update:open="
      (v) => {
        isOpen = v;
        if (!v) emit('close');
      }
    "
    @update:model-value="
      (value: DisputeReason | DisputeReason[] | null) => {
        if (value && !Array.isArray(value)) {
          selectedReason = value;
          emit('update', value);
          onUpdate?.(value);
        }
      }
    "
  >
    <template #trigger>
      <Button
        class="w-full justify-between"
        :class="triggerClass"
        :disabled="disabled"
        :size="size"
        variant="outline"
      >
        <Tooltip v-if="selectedReason">
          <TooltipTrigger class="overflow-hidden truncate">
            <span class="whitespace-nowrap truncate">
              <strong>{{ selectedReason.code }}</strong>

              <template v-if="selectedReason.name"> - {{ selectedReason.name }} </template>
            </span>
          </TooltipTrigger>

          <TooltipContent side="top">
            <span class="whitespace-nowrap truncate">
              {{ selectedReason.code }}

              <template v-if="selectedReason.name"> - {{ selectedReason.name }} </template>
            </span>
          </TooltipContent>
        </Tooltip>

        <template v-else> Select a Reason </template>
        <i class="fal fa-chevron-down text-3xs"></i>
      </Button>
    </template>

    <template #empty>
      <div class="px-4 text-muted-foreground w-70 flex flex-col gap-y-2">
        <template v-if="profileStore?.profile?.is_org_admin">
          <span>No reason codes yet. Set them up to create disputes.</span>
          <RouterLink :to="{ name: 'playbook.disputes.codes' }" as-child>
            <Button variant="outline" size="sm" class="w-fit">Set up reason codes</Button>
          </RouterLink>
        </template>
        <template v-else>Ask your admin to setup reason codes to create a dispute.</template>
      </div>
    </template>

    <template #selected="{ value }">
      <span class="whitespace-nowrap truncate">
        <strong>{{ value.code }}</strong>

        <template v-if="value.name"> - {{ value.name }} </template>
      </span>
    </template>

    <template #option="{ option }">
      <div
        class="flex flex-1 items-center justify-between truncate gap-x-4"
        :class="maxOptionWidth"
      >
        <span class="line-clamp-1 truncate">
          <strong>{{ option?.code }}</strong>

          <template v-if="option?.name"> - {{ option.name }} </template>
        </span>

        <Tooltip v-if="!option.is_active">
          <TooltipTrigger>
            <i class="far fa-archive" />
          </TooltipTrigger>

          <TooltipContent side="top"> Archived </TooltipContent>
        </Tooltip>
      </div>
    </template>

    <template v-if="profileStore?.profile?.is_org_admin" #fixedBottom>
      <Button
        variant="ghost"
        class="w-full justify-start text-left border-t"
        :size="size"
        @click="addNewDisputeReason"
      >
        <i class="fa-solid fa-plus mr-2"></i>
        Add New Reason
      </Button>
    </template>
  </ComboSelect>

  <DisputeReasonDialog
    ref="reasonDialogRef"
    @complete="updateNewDisputeReason"
    @close="ReasonDialogRef?.hide()"
  />
</template>
